import React from 'react';
import { Star } from 'lucide-react';
import { ExperienceIconContainer, resolveAccent, isRegistryKey } from '../icons';
import type { ExperienceAccent, TileIcon } from '../icons';

export interface AppTileData {
	path: string;
	label: string;
	group?: string;
	icon?: string | null;
	accent?: ExperienceAccent | null;
	description?: string;
	badge?: string | null;
	/** "count" renders the number; "dot" renders an attention dot (entitlement audiences). */
	badgeMode?: 'count' | 'dot';
	/** Accessible label for the badge (e.g. "3 items need attention"). */
	badgeAriaLabel?: string;
	disabled?: boolean;
	disabledReason?: string;
}

export interface ExperienceAppTileProps {
	app: AppTileData;
	featured?: boolean;
	isFavorite?: boolean;
	brandIcon?: TileIcon;
	onNavigate?: (path: string) => void;
	onToggleFavorite?: (path: string) => void;
}

// Sized to fit a single-line title plus a two-line description (icon 36/44 px)
// without clipping — the description now carries real meaning, not 3 words.
const TILE_HEIGHT = 84;
const FEATURED_TILE_HEIGHT = 96;

export function ExperienceAppTile({
	app,
	featured,
	isFavorite,
	brandIcon,
	onNavigate,
	onToggleFavorite,
}: ExperienceAppTileProps) {
	const height = featured ? FEATURED_TILE_HEIGHT : TILE_HEIGHT;
	const iconSize = featured ? 44 : 36;
	const innerIconSize = featured ? 24 : 20;
	const accent = resolveAccent(app.accent, app.group);

	const resolvedIcon: TileIcon = brandIcon
		?? (isRegistryKey(app.icon)
			? { kind: 'registry', key: app.icon, accent }
			: { kind: 'registry', key: 'app-window', accent });

	const handleClick = () => {
		if (!app.disabled && onNavigate) onNavigate(app.path);
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if ((e.key === 'Enter' || e.key === ' ') && !app.disabled) {
			e.preventDefault();
			onNavigate?.(app.path);
		}
	};

	const handleFavClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		onToggleFavorite?.(app.path);
	};

	return (
		<div
			className={`exp-app-tile${featured ? ' exp-app-tile--featured' : ''}${app.disabled ? ' exp-app-tile--disabled' : ''}`}
			onClick={handleClick}
			onKeyDown={handleKeyDown}
			role="button"
			tabIndex={app.disabled ? -1 : 0}
			aria-label={app.disabled ? `${app.label} (unavailable)` : `Open ${app.label}`}
			aria-disabled={app.disabled || undefined}
			data-testid={`app-tile-${app.path.replace(/\//g, '-').replace(/^-/, '')}`}
			style={{ height, minHeight: height }}
		>
			<div className="exp-app-tile__body">
				{resolvedIcon.kind === 'registry' ? (
					<ExperienceIconContainer
						iconKey={resolvedIcon.key}
						accent={resolvedIcon.accent}
						group={app.group}
						containerSize={iconSize}
						iconSize={innerIconSize}
						className="exp-app-tile__icon"
					/>
				) : (
					<div
						className="exp-app-tile__icon exp-app-tile__brand-icon"
						style={{ width: iconSize, height: iconSize }}
					>
						{resolvedIcon.src ? (
							<img
								src={resolvedIcon.src}
								alt={resolvedIcon.alt ?? app.label}
								style={{ width: innerIconSize, height: innerIconSize, objectFit: 'contain' }}
							/>
						) : (
							<span className="exp-app-tile__initials">
								{resolvedIcon.initials ?? app.label.charAt(0).toUpperCase()}
							</span>
						)}
					</div>
				)}

				<div className="exp-app-tile__text">
					<div className="exp-app-tile__title" title={app.label}>
						{app.label}
					</div>
					{app.description ? (
						<div className="exp-app-tile__desc" title={app.description}>
							{app.description}
						</div>
					) : app.group ? (
						<div className="exp-app-tile__category">
							{app.group}
						</div>
					) : null}
				</div>

				{(app.badgeMode === 'dot' || (app.badge != null && app.badge !== '')) && (
					<span
						className={`exp-app-tile__badge${app.badgeMode === 'dot' ? ' exp-app-tile__badge--dot' : ''}`}
						data-testid={`app-tile-badge-${app.path.replace(/\//g, '-').replace(/^-/, '')}`}
						role="status"
						aria-label={app.badgeAriaLabel || (app.badge ? String(app.badge) : 'Attention needed')}
					>
						{app.badgeMode === 'dot' ? '' : app.badge}
					</span>
				)}

				{onToggleFavorite && (
					<button
						className="exp-app-tile__fav"
						onClick={handleFavClick}
						aria-pressed={isFavorite}
						aria-label={isFavorite ? `Remove ${app.label} from favorites` : `Add ${app.label} to favorites`}
						data-testid={`fav-btn-${app.path.replace(/\//g, '-').replace(/^-/, '')}`}
					>
						<Star size={13} fill={isFavorite ? 'currentColor' : 'none'} strokeWidth={isFavorite ? 0 : 1.5} />
					</button>
				)}
			</div>
		</div>
	);
}
